/// src/firebase.js
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { isSupported as isAnalyticsSupported } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

/** Lazy Analytics — skip when measurementId missing; suppress auto page_view on init. */
export let analytics = null;
if (typeof window !== 'undefined' && firebaseConfig.measurementId) {
  isAnalyticsSupported()
    .then(async (supported) => {
      if (!supported) return null;
      const { initializeAnalytics } = await import('firebase/analytics');
      analytics = initializeAnalytics(app, {
        config: {
          send_page_view: false,
        },
      });
      return analytics;
    })
    .catch((err) => {
      console.warn('Firebase Analytics unavailable:', err);
      return null;
    });
}

/** Lazy Messaging — only initialize when supported in this browser. */
export let messaging = null;
if (typeof window !== 'undefined') {
  isMessagingSupported()
    .then((supported) => {
      if (!supported) return null;
      messaging = getMessaging(app);
      return messaging;
    })
    .catch((err) => {
      console.warn('Firebase Messaging unavailable:', err);
      return null;
    });
}

export const functions = getFunctions(app, 'us-central1');

const isLocalHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

if (isLocalHost) {
  const useFullEmulatorStack =
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('useFirebaseEmulators') === '1';

  const useFunctionsEmulator =
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('useFunctionsEmulator') === '1';

  if (useFullEmulatorStack) {
    console.log('Firebase: emulators (auth, firestore, functions, storage)');
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    connectStorageEmulator(storage, '127.0.0.1', 9199);
  } else if (useFunctionsEmulator) {
    console.log('Firebase: Functions emulator (127.0.0.1:5001), prod Auth/Firestore');
    console.log('Start with: firebase emulators:start --only functions');
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  } else {
    console.log(
      'Firebase: production Auth, Firestore, and Functions. ' +
        'For local functions: localStorage.setItem(\'useFunctionsEmulator\', \'1\')',
    );
  }
}
